import type { ReactNode } from 'react';

import { Badge } from '@lilith-arcana-os/ui';
import { motion } from 'framer-motion';
import { Archive, BookOpen, Home, Settings, Sparkles } from 'lucide-react';

import type { AppPage, NetworkStatus, PWAStatus, UserSession } from '@lilith-arcana-os/domain';

import { navigationPages } from '../../lib/constants';
import { OfflineBanner } from '../ritual/OfflineBanner';

const navIcons = {
  home: Home,
  reading: Sparkles,
  library: BookOpen,
  history: Archive,
  settings: Settings,
  login: Home,
} as const;

interface AppShellProps {
  children: ReactNode;
  currentPage: AppPage;
  llmStatus: 'ready' | 'unavailable' | 'fallback' | 'testing';
  networkStatus: NetworkStatus;
  onNavigate: (page: AppPage) => void;
  pwaStatus: PWAStatus;
  session: UserSession;
}

export function AppShell({
  children,
  currentPage,
  llmStatus,
  networkStatus,
  onNavigate,
  pwaStatus,
  session,
}: AppShellProps) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[var(--la-bg)] text-[var(--la-text)]">
      <div className="pointer-events-none absolute inset-0 bg-veil opacity-80" />
      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-4 pb-28 pt-6 sm:px-6">
        <header className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[var(--la-text-muted)]">
              Lilith Arcana OS
            </p>
            <h1 className="font-heading text-2xl text-[var(--la-text)]">WordPress-native ritual shell</h1>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Badge tone="gold">{networkStatus}</Badge>
            <Badge tone={llmStatus === 'fallback' ? 'crimson' : 'muted'}>{llmStatus}</Badge>
            <Badge tone="muted">{pwaStatus}</Badge>
            {session.isLoggedIn ? <Badge tone="muted">{session.displayName ?? 'reader'}</Badge> : null}
          </div>
        </header>

        <OfflineBanner llmStatus={llmStatus} networkStatus={networkStatus} />

        <motion.main
          animate={{ opacity: 1, y: 0 }}
          className="flex-1"
          initial={{ opacity: 0, y: 12 }}
          key={currentPage}
          transition={{ duration: 0.28 }}
        >
          {children}
        </motion.main>

        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-white/5 bg-[rgba(10,10,15,0.96)] backdrop-blur">
          <div className="mx-auto grid max-w-6xl grid-cols-5 gap-2 px-3 py-3">
            {navigationPages.map((item) => {
              const Icon = navIcons[item.id];
              const active = currentPage === item.id;

              return (
                <button
                  className={`flex flex-col items-center gap-1 rounded-2xl px-2 py-3 text-[0.7rem] uppercase tracking-[0.16em] transition ${
                    active
                      ? 'bg-[rgba(201,169,110,0.14)] text-[var(--la-gold)]'
                      : 'text-[var(--la-text-muted)]'
                  }`}
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  type="button"
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
